import React, { useState, useEffect, useRef, useCallback } from "react";
import { setAppBit, getCounter, incrementCounter, getLogs, addLog as addLogAPI } from "../api/sacada";
import useWebSocket from "../hooks/useWebSockets";
import { useAuth } from "../context/AuthContext";

/* ============================================================
   FEUX TRICOLORES — 4 DIRECTIONS
   MEA PULSE SCADA · PLC Crouzet Millenium MXD12RD7ET

   Registre de commande (écriture, adresse 25) :
     bit 0 = marche
     bit 1 = arret
     bit 2 = capteur_forcage   ← capteur de forçage unique (bascule 0/1)

   Registre de statut (lecture, adresse 50) :
     bit 0 = marche
     bit 1 = arret
     bit 2 = capteur_forcage
     bit 8  = led_vert_ns
     bit 9  = led_orange_ns
     bit 10 = led_rouge_ns
     bit 11 = led_vert_eo
     bit 12 = led_orange_eo
     bit 13 = led_rouge_eo

   ⚠️ Il n'y a plus de bit dédié au "reset" (S3) dans la config actuelle.
   Le bouton Reset reste dans l'UI mais n'envoie plus de commande PLC tant
   qu'un bit "reset" n'est pas ajouté au registre de commande (25).
   ============================================================ */

const DURATIONS = { NS_GREEN: 15, NS_ORANGE: 4, EO_GREEN: 15, EO_ORANGE: 4 };
const STEP_LABEL = {
  NS_GREEN: "NS Vert",
  NS_ORANGE: "NS Orange",
  EO_GREEN: "EO Vert",
  EO_ORANGE: "EO Orange",
};

// Index du bit "capteur_forcage" dans le registre de commande (25) / statut (50)
const FORCAGE_BIT = 2;
const COMMAND_PULSE_MS = 500;

function nowStr() {
  return new Date().toLocaleTimeString("fr-FR", { hour12: false });
}

// Les entrées de journal chargées depuis la base ne stockent qu'un message
// texte (pas d'icône) — on déduit un type d'icône cohérent à partir du texte,
// pour garder le même rendu visuel qu'avant.
function guessLogType(message) {
  if (/démarré/i.test(message)) return "play";
  if (/arrêté/i.test(message)) return "stop";
  if (/activé/i.test(message)) return "up";
  if (/désactivé/i.test(message)) return "down";
  return "info";
}

function ForceButton({ on, label, sublabel, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className={`ft-force-btn ${on ? "on" : "off"}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
    >
      <span className="ft-force-btn-top">
        <span className="ico">{on ? "▶" : "■"}</span>
        {label}
      </span>
      {sublabel && <span className="ft-force-btn-sub">{sublabel}</span>}
    </button>
  );
}

const LOG_ICON = {
  up: "▲",
  down: "▼",
  play: "▶",
  stop: "■",
  reset: "↻",
  info: "ℹ",
};

export default function FeuxTricoloresSCADA({ inactivePreview = false }) {
  const { token } = useAuth();
  const {
    data,
    lastMessage,
    connected,
    stale,
    error: liveDataError,
  } = useWebSocket("feu_tricolore", !inactivePreview);
  const pendingCommandTraces = useRef(new Map());
  const [, setLastCommandLatencyMs] = useState(null);

  const [elapsed, setElapsed] = useState(0);
  // ── Compteur de secondes affiché dans chaque case du cycle (remplace les durées fixes 15s/4s) ──
  const [stepSeconds, setStepSeconds] = useState({
    NS_GREEN: 0, NS_ORANGE: 0, EO_GREEN: 0, EO_ORANGE: 0,
  });
  const [log, setLog] = useState([
    { id: 0, time: nowStr(), text: "Système initialisé — en attente de marche", type: "info" },
  ]);
  const logIdRef = useRef(1);
  const prevStepRef = useRef(null);
  const prevMarche = useRef(false);
  const prevArret = useRef(false);

  const sendTracedCommand = useCallback(async (bit, state) => {
    const correlationId = window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    pendingCommandTraces.current.set(correlationId, performance.now());
    try {
      return await setAppBit("feu_tricolore", bit, state, token, correlationId);
    } catch (error) {
      pendingCommandTraces.current.delete(correlationId);
      throw error;
    }
  }, [token]);

  useEffect(() => {
    const traces = lastMessage?.command_traces || [];
    traces.forEach((trace) => {
      const clickStartedAt = pendingCommandTraces.current.get(trace.correlation_id);
      if (clickStartedAt === undefined) return;
      pendingCommandTraces.current.delete(trace.correlation_id);
      requestAnimationFrame(() => {
        const clickToScreenMs = performance.now() - clickStartedAt;
        setLastCommandLatencyMs(Number(clickToScreenMs.toFixed(2)));
        console.info("[PERF][feu_tricolore]", {
          ...trace,
          click_to_screen_ms: Number(clickToScreenMs.toFixed(2)),
        });
      });
    });
  }, [lastMessage]);

  // ── Compteur de changements de sens (persistant, en base) ──
  const [forcageCount, setForcageCount] = useState(0);
  // ── Compteur "depuis l'ouverture de la page" — jamais persisté, repart à 0
  // à chaque rafraîchissement (même principe que sur Barriere.jsx / Reservoir.jsx).
  const [sessionForcageCount, setSessionForcageCount] = useState(0);

  // ── Forçage : impulsion momentanée de 500 ms, comme Marche/Arrêt ──
  // localForcage donne un retour visuel immédiat pendant l'impulsion, sans
  // remplacer la valeur réelle relue dans le registre de statut 50.
  const [localForcage, setLocalForcage] = useState(null);
  const [isSendingForcage, setIsSendingForcage] = useState(false);

  const addLog = useCallback((text, type) => {
    setLog((prev) => [{ id: logIdRef.current++, time: nowStr(), text, type }, ...prev].slice(0, 8));
    // Persiste l'entrée en base pour qu'elle survive à un rafraîchissement de page.
    addLogAPI("feu_tricolore", text).catch((err) =>
      console.error("Erreur enregistrement journal :", err)
    );
  }, []);

  // Charge, au montage, le journal déjà enregistré et le total du compteur
  // de changements de sens — pour ne plus repartir de zéro à chaque refresh.
  useEffect(() => {
    getLogs("feu_tricolore", 8)
      .then((entries) => {
        if (entries.length === 0) return; // garde le message d'accueil par défaut
        setLog(
          entries.map((e) => ({
            id: logIdRef.current++,
            time: new Date(e.timestamp).toLocaleTimeString("fr-FR", { hour12: false }),
            text: e.message,
            type: guessLogType(e.message),
          }))
        );
      })
      .catch((err) => console.error("Erreur chargement journal :", err));

    getCounter("feu_tricolore", "changements_de_sens")
      .then((res) => setForcageCount(res.value))
      .catch((err) => console.error("Erreur chargement compteur forçage :", err));
  }, []);

  // ── États réels venant du PLC (registre de statut, adresse 50) ──
  const marche          = data?.marche ?? false;
  const arret            = data?.arret ?? false;
  const capteurForcage   = data?.capteur_forcage ?? false;

  const displayForcage = localForcage !== null ? localForcage : capteurForcage;

  // Dès que le PLC confirme la valeur, on efface l'override local.
  useEffect(() => {
    if (localForcage !== null && capteurForcage === localForcage) setLocalForcage(null);
  }, [capteurForcage, localForcage]);

  const ledVertNS   = data?.led_vert_ns ?? false;
  const ledOrangeNS = data?.led_orange_ns ?? false;
  const ledRougeNS  = data?.led_rouge_ns ?? false;
  const ledVertEO   = data?.led_vert_eo ?? false;
  const ledOrangeEO = data?.led_orange_eo ?? false;
  const ledRougeEO  = data?.led_rouge_eo ?? false;

  const nsColor = ledVertNS ? "green" : ledOrangeNS ? "orange" : ledRougeNS ? "red" : "off";
  const eoColor = ledVertEO ? "green" : ledOrangeEO ? "orange" : ledRougeEO ? "red" : "off";
  const running = nsColor !== "off" || eoColor !== "off";
  const isForcing = displayForcage;
  const commandsEnabled = lastMessage?.type === "live_data"
    && lastMessage?.commands_enabled === true;
  const controlsDisabled = !connected || stale || data === null || !commandsEnabled;
  const nsMoving  = nsColor === "green";
  const eoMoving  = eoColor === "green";

  const effectiveStep =
    nsColor === "green"  ? "NS_GREEN"  :
    nsColor === "orange" ? "NS_ORANGE" :
    eoColor === "green"  ? "EO_GREEN"  :
    eoColor === "orange" ? "EO_ORANGE" : null;

  /* ---------------- Chrono cosmétique (resynchronisé sur chaque vrai changement de feu) ---------------- */
  useEffect(() => {
    if (effectiveStep !== prevStepRef.current) {
      setElapsed(0);
      prevStepRef.current = effectiveStep;
    }
  }, [effectiveStep]);

  useEffect(() => {
    if (!running || isForcing || !effectiveStep) return;
    const id = setInterval(() => setElapsed((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running, isForcing, effectiveStep]);

  // Reporte le chrono courant dans la case correspondante — affichage uniquement, n'affecte rien d'autre.
  useEffect(() => {
    if (!effectiveStep) return;
    setStepSeconds((prev) => ({ ...prev, [effectiveStep]: elapsed }));
  }, [elapsed, effectiveStep]);

  const totalDuration = DURATIONS[effectiveStep] || 15;
  const timeLeft = Math.max(0, totalDuration - elapsed);
  const progressPct = isForcing ? 100 : effectiveStep ? Math.min(100, (elapsed / totalDuration) * 100) : 0;

  /* ---------------- Commandes → PLC réel ---------------- */
  async function handleStart() {
    if (running) return;
    try {
      await sendTracedCommand(0, true);
      setTimeout(
        () => sendTracedCommand(0, false).catch((err) => console.error("Erreur PLC :", err)),
        500
      );
    } catch (err) { console.error("Erreur PLC :", err); }
  }
  async function handleStop() {
    if (!running) return;
    try {
      await sendTracedCommand(1, true);
      setTimeout(
        () => sendTracedCommand(1, false).catch((err) => console.error("Erreur PLC :", err)),
        500
      );
    } catch (err) { console.error("Erreur PLC :", err); }
  }
  // Une action utilisateur produit exactement une impulsion : bit 2 à 1, attente
  // de 500 ms, puis remise automatique à 0. Le bouton reste verrouillé pendant
  // toute la séquence pour empêcher deux impulsions concurrentes.
  async function pulseForcage() {
    if (isSendingForcage) return;
    setIsSendingForcage(true);
    setLocalForcage(true);
    addLog("Impulsion de forçage envoyée", "up");

    let activationSucceeded = false;
    try {
      await sendTracedCommand(FORCAGE_BIT, true);
      activationSucceeded = true;
      await new Promise((resolve) => setTimeout(resolve, COMMAND_PULSE_MS));
      await sendTracedCommand(FORCAGE_BIT, false);
    } catch (err) {
      console.error("Erreur PLC :", err);
    } finally {
      setLocalForcage(null);
      setIsSendingForcage(false);
    }

    if (activationSucceeded) {
      setSessionForcageCount((c) => c + 1);

      try {
        const res = await incrementCounter("feu_tricolore", "changements_de_sens");
        setForcageCount(res.value);
      } catch (err) {
        console.error("Erreur enregistrement compteur forçage :", err);
      }
    }
  }

  /* ---------------- Journal — déclenché sur les vrais fronts montants ---------------- */
  useEffect(() => {
    if (marche && !prevMarche.current) addLog("Système démarré — cycle automatique (S1)", "play");
    prevMarche.current = marche;
  }, [marche, addLog]);

  useEffect(() => {
    if (arret && !prevArret.current) addLog("Système arrêté (S2)", "stop");
    prevArret.current = arret;
  }, [arret, addLog]);

  const plcBits = [
    { label: "I1  Marche", value: marche ? 1 : 0 },
    { label: "I2  Arrêt", value: arret ? 1 : 0 },
    { label: "I3 Forçage", value: capteurForcage ? 1 : 0 },
  ];

  const plcOutputs = [
    { label: "O1 — NS Rouge", value: nsColor === "red" ? 1 : 0, color: "#ff4d4f" },
    { label: "O2 — NS Orange", value: nsColor === "orange" ? 1 : 0, color: "#ffb020" },
    { label: "O3 — NS Vert", value: nsColor === "green" ? 1 : 0, color: "#2ecc71" },
    { label: "O4 — EO Rouge", value: eoColor === "red" ? 1 : 0, color: "#ff4d4f" },
    { label: "O5 — EO Orange", value: eoColor === "orange" ? 1 : 0, color: "#ffb020" },
    { label: "O6 — EO Vert", value: eoColor === "green" ? 1 : 0, color: "#2ecc71" },
  ];

  /* ============================================================
     RENDU
     ============================================================ */
  return (
    <div className="ft-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Share+Tech+Mono&display=swap');

        .ft-root {
          --teal: #057c82;
          --teal-dark: #045f64;
          --teal-light: #e3f2f1;
          --gray: #555454;
          --gray-light: #f4f5f5;
          --white: #ffffff;
          --line: #e3e5e5;
          --red: #ff4d4f;
          --red-off: #5a2b2d;
          --orange: #ffb020;
          --orange-off: #5c4a23;
          --green: #2ecc71;
          --green-off: #234a33;

          font-family: 'Rajdhani', sans-serif;
          background: var(--gray-light);
          color: var(--gray);
          width: 100%;
          min-width: 0;
          padding: clamp(8px, 2vw, 16px);
          box-sizing: border-box;
          max-width: 1460px;
          margin: 0 auto;
        }
        .ft-root * { box-sizing: border-box; }
        .ft-mono { font-family: 'Share Tech Mono', monospace; }

        .ft-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--white);
          border: 1px solid var(--line);
          border-left: 4px solid var(--teal);
          border-radius: 10px;
          padding: 14px 20px;
          margin-bottom: 14px;
          flex-wrap: wrap;
          gap: 10px;
        }
        .ft-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
        .ft-header-icon {
          width: 38px; height: 38px; border-radius: 9px;
          background: var(--teal); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .ft-title { font-size: clamp(15px, 2.4vw, 18px); font-weight: 700; letter-spacing: 0.5px; color: var(--gray); margin: 0; overflow-wrap: anywhere; }
        .ft-subtitle { font-size: 12px; color: #8a8a8a; margin: 2px 0 0; font-family: 'Share Tech Mono', monospace; }
        .ft-force-badge {
          display: flex; align-items: center; gap: 6px;
          background: #fff4e0; color: #b96a00; border: 1px solid #ffd9a0;
          border-radius: 20px; padding: 6px 14px; font-weight: 600; font-size: 13px;
          white-space: nowrap;
        }
        .ft-header-metrics { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex-wrap: wrap; min-width: 0; }
        .ft-latency-badge {
          background: var(--teal-light); color: var(--teal-dark); border: 1px solid #b9dedd;
          border-radius: 20px; padding: 6px 12px; font: 700 11px 'Share Tech Mono', monospace;
          white-space: nowrap;
        }
        .ft-health-detail {
          width: 100%; margin-top: -4px; color: #9a5a00;
          font: 600 10px 'Share Tech Mono', monospace; text-align: right;
          overflow-wrap: anywhere;
        }
        .ft-connection-alert {
          display: flex; align-items: flex-start; gap: 9px;
          margin: 0 0 14px; padding: 10px 12px; border-radius: 9px;
          background: #fff8e8; color: #7d4d00; border: 1px solid #f2d28f;
          font: 600 12px/1.4 'Share Tech Mono', monospace;
          overflow-wrap: anywhere;
        }
        .ft-connection-alert.disconnected { background: #fff0f0; color: #8e292c; border-color: #ffc7c8; }

        .ft-progress-card {
          background: var(--white); border: 1px solid var(--line); border-radius: 10px;
          padding: 14px 18px; margin-bottom: 14px;
        }
        .ft-progress-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;}
        .ft-progress-title { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; color: var(--gray); }
        .ft-progress-title .pill {
          background: var(--teal-light); color: var(--teal-dark); font-size: 11px; font-weight: 700;
          padding: 2px 9px; border-radius: 12px; letter-spacing: 0.5px;
        }
        .ft-timer { font-family: 'Share Tech Mono', monospace; font-size: 13px; color: var(--teal-dark); font-weight: 700; }

        .ft-steps { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-bottom: 10px; }
        .ft-step-box {
          text-align: center; padding: 8px 4px; border-radius: 8px;
          background: var(--gray-light); border: 1px solid var(--line);
          transition: all 0.25s ease;
        }
        .ft-step-box.active { background: #e6effd; border-color: #a9c8f5; }
        .ft-step-box .lbl { font-size: 12px; font-weight: 700; color: var(--gray); }
        .ft-step-box .dur { font-size: 11px; color: #9a9a9a; font-family: 'Share Tech Mono', monospace; }
        .ft-step-box.active .lbl { color: #2f6fed; }
        .ft-step-box.active .dur { color: #2f6fed; }

        .ft-bar-row { display: flex; align-items: center; gap: 10px; }
        .ft-bar-label { font-size: 11px; font-weight: 700; color: var(--gray); white-space: nowrap; min-width: 70px; }
        .ft-bar-track { flex: 1; height: 9px; background: var(--gray-light); border-radius: 6px; overflow: hidden; position: relative; }
        .ft-bar-fill { height: 100%; border-radius: 6px; background: linear-gradient(90deg, var(--teal), #0aa3a9); transition: width 0.9s linear; }
        .ft-bar-fill.forced {
          background: repeating-linear-gradient(45deg, var(--teal), var(--teal) 10px, #0aa3a9 10px, #0aa3a9 20px);
          background-size: 200% 100%;
          animation: ft-stripes 1s linear infinite;
        }
        @keyframes ft-stripes { from { background-position: 0 0; } to { background-position: 28px 0; } }
        .ft-bar-end { font-size: 10px; font-weight: 700; color: #b96a00; min-width: 36px; text-align: right; letter-spacing: 0.5px; }

        .ft-grid { display: grid; grid-template-columns: minmax(280px, 320px) minmax(0, 1fr); gap: 16px; align-items: start; }
        @media (max-width: 960px) { .ft-grid { grid-template-columns: 1fr; } }
        .ft-controls-col { min-width: 0; }

        /* ── Colonne droite : vue intersection + journal empilés ── */
        .ft-right-col { display: flex; flex-direction: column; gap: 14px; }

        .ft-card {
          background: var(--white); border: 1px solid var(--line); border-radius: 10px;
          padding: 14px; margin-bottom: 14px;
        }
        .ft-right-col .ft-card { margin-bottom: 0; }
        .ft-card-title {
          display: flex; align-items: center; justify-content: space-between; gap: 7px;
          font-size: 12.5px; font-weight: 700; color: var(--gray);
          text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 12px;
        }
        .ft-card-title-left { display: flex; align-items: center; gap: 7px; }
        .ft-card-title-badge {
          background: var(--teal-light); color: var(--teal-dark);
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.3px;
          padding: 2px 9px; border-radius: 12px; text-transform: none;
        }

        .ft-btn-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 8px; }
        .ft-btn {
          min-height: 52px; border: none; border-radius: 8px; padding: 12px 6px; cursor: pointer;
          font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 13px;
          display: flex; flex-direction: column; align-items: center; gap: 3px;
          transition: transform 0.1s ease, opacity 0.2s ease;
        }
        .ft-btn:active { transform: scale(0.96); }
        .ft-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .ft-btn .ico { font-size: 16px; }
        .ft-btn .sub { font-size: 9px; font-weight: 500; opacity: 0.8; font-family: 'Share Tech Mono', monospace; }
        .ft-btn.start { background: #16322c; color: #4ee08a; }
        .ft-btn.stop { background: #2b2c2c; color: #ff8d8f; }
        .ft-btn.reset {
          width: 100%; background: var(--gray-light); color: var(--gray);
          border: 1px solid var(--line); flex-direction: row; justify-content: center; gap: 8px;
        }

        /* ── Bouton industriel ON/OFF pour le capteur de forçage ── */
        .ft-force-btn {
          width: 100%;
          min-height: 52px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 2px;
          padding: 11px 13px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          font-family: 'Rajdhani', sans-serif;
          transition: background 0.18s, color 0.18s, box-shadow 0.18s, transform 0.1s;
          margin-bottom: 9px;
        }
        .ft-force-btn:last-of-type { margin-bottom: 10px; }
        .ft-force-btn:active { transform: scale(0.97); }
        .ft-force-btn:disabled { opacity: 0.55; cursor: not-allowed; }

        .ft-force-btn.off {
          background: var(--gray-light);
          border: 1px solid var(--line);
          color: var(--gray);
        }
        .ft-force-btn.off:hover:not(:disabled) { background: #ececec; }

        .ft-force-btn.on {
          background: #3b2b10;
          color: #ffb020;
          box-shadow: 0 0 0 2px #ffb02040, 0 4px 14px #ffb02033;
        }

        .ft-force-btn-top {
          display: flex; align-items: center; gap: 8px;
          font-size: 13px; font-weight: 700;
        }
        .ft-force-btn-top .ico { font-size: 12px; }
        .ft-force-btn-sub {
          font-size: 10px; font-weight: 500; opacity: 0.75;
          font-family: 'Share Tech Mono', monospace;
        }

        /* ── Compteur de changements de sens (appuis sur le forçage) ── */
        .ft-force-counter {
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
          background: var(--teal-light); border: 1px solid #b9dedd;
          border-radius: 8px; padding: 9px 12px; margin-top: 2px;
        }
        .ft-force-counter .lbl {
          font-size: 11px; font-weight: 700; color: var(--teal-dark);
          display: flex; align-items: center; gap: 6px; overflow-wrap: anywhere;
        }
        .ft-force-counter .val {
          font-family: 'Share Tech Mono', monospace; font-size: 18px; font-weight: 800;
          color: var(--teal-dark);
        }

        /* ── Compteur "depuis ouverture page" (non persisté) ── */
        .ft-force-counter.session {
          background: #fff4e0; border: 1px solid #ffd9a0; margin-top: 8px;
        }
        .ft-force-counter.session .lbl { color: #b96a00; }
        .ft-force-counter.session .val { color: #b96a00; }

        .ft-plc-status { display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 10px; font-size: 10px; color: #9a9a9a; letter-spacing: 1px; }
        .ft-plc-dot { width: 6px; height: 6px; border-radius: 50%; background: #cfd2d2; }
        .ft-plc-dot.on { background: var(--teal); animation: ft-pulse 1.4s ease-in-out infinite; }
        @keyframes ft-pulse { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }

        .ft-plc-row { display: flex; align-items: center; justify-content: space-between; padding: 7px 0; font-size: 12.5px; }
        .ft-plc-row .dot { width: 8px; height: 8px; border-radius: 50%; background: #d2d4d4; margin-right: 8px; flex-shrink: 0; }
        .ft-plc-row .dot.on { background: var(--teal); box-shadow: 0 0 5px var(--teal); }
        .ft-plc-row .name { flex: 1; color: var(--gray); display: flex; align-items: center; }
        .ft-plc-row .val { font-family: 'Share Tech Mono', monospace; font-weight: 700; color: #b5b8b8; }
        .ft-plc-row .val.on { color: var(--teal-dark); }

        /* ── Journal : nouvelle mise en page, colonne droite ── */
        .ft-log-list {
          display: flex; flex-direction: column; gap: 7px;
          max-height: 320px; overflow-y: auto; padding-right: 4px;
        }
        .ft-log-list::-webkit-scrollbar { width: 6px; }
        .ft-log-list::-webkit-scrollbar-thumb { background: #d5d8d8; border-radius: 4px; }
        .ft-log-list::-webkit-scrollbar-track { background: transparent; }

        .ft-log-entry {
          display: flex; align-items: flex-start; gap: 9px;
          padding: 8px 10px; border-radius: 8px;
          background: #fbf8f0; border: 1px solid #f1e6c8;
        }
        .ft-log-entry.play { background: var(--teal-light); border-color: #b9dedd; }
        .ft-log-entry.stop { background: #f4f0f0; border-color: #e3dada; }
        .ft-log-entry.reset { background: #eef2fb; border-color: #cfdcf3; }

        .ft-log-icon-badge {
          flex-shrink: 0; width: 24px; height: 24px; border-radius: 7px;
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 700; color: #b96a00; background: #ffedcf;
        }
        .ft-log-icon-badge.play { color: var(--teal-dark); background: #cdeceb; }
        .ft-log-icon-badge.stop { color: #6b6b6b; background: #e4e0e0; }
        .ft-log-icon-badge.reset { color: #3a5fbf; background: #dbe4f8; }
        .ft-log-icon-badge.info { color: #8a8a8a; background: #eceeee; }

        .ft-log-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
        .ft-log-time {
          align-self: flex-start;
          font-size: 10px; font-weight: 700; letter-spacing: 0.4px;
          color: #9a8355; background: rgba(255,255,255,0.6);
          border-radius: 5px; padding: 1px 6px;
        }
        .ft-log-entry.play .ft-log-time { color: var(--teal-dark); }
        .ft-log-entry.stop .ft-log-time { color: #7a7a7a; }
        .ft-log-entry.reset .ft-log-time { color: #3a5fbf; }
        .ft-log-text { font-size: 12px; color: var(--gray); line-height: 1.35; }

        .ft-view-card { background: var(--white); border: 1px solid var(--line); border-radius: 10px; padding: 16px; }
        .ft-view-title { display: flex; align-items: center; gap: 7px; font-weight: 700; font-size: 13px; color: var(--gray); margin-bottom: 12px; }
        .ft-svg-wrap {
          width: min(100%, clamp(360px, 58vh, 520px));
          margin: 0 auto;
          aspect-ratio: 1 / 1;
          border-radius: 10px;
          overflow: hidden;
          background: #dfe4e4;
          position: relative;
        }
        .ft-svg-wrap svg { width: 100%; height: 100%; display: block; }
        .ft-svg-wrap.stopped::after {
          content: 'SYSTÈME ARRÊTÉ'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
          background: rgba(255,255,255,0.55); color: var(--gray); font-weight: 700; letter-spacing: 1px; font-size: 13px;
          font-family: 'Share Tech Mono', monospace;
        }

        .ft-car { transition: transform 0.6s ease-out, opacity 0.35s ease-out; }
        .ft-car.drive-s { animation: ft-drive-s 5.5s linear infinite; }
        .ft-car.drive-n { animation: ft-drive-n 5.5s linear infinite; }
        .ft-car.drive-w { animation: ft-drive-w 5.5s linear infinite; }
        .ft-car.drive-e { animation: ft-drive-e 5.5s linear infinite; }
        @keyframes ft-drive-s {
          0% { transform: translate(0,0); opacity: 0; }
          6% { opacity: 1; }
          88% { opacity: 1; }
          96% { transform: translate(0,452px); opacity: 0; }
          100% { transform: translate(0,452px); opacity: 0; }
        }
        @keyframes ft-drive-n {
          0% { transform: translate(0,0); opacity: 0; }
          6% { opacity: 1; }
          88% { opacity: 1; }
          96% { transform: translate(0,-452px); opacity: 0; }
          100% { transform: translate(0,-452px); opacity: 0; }
        }
        @keyframes ft-drive-w {
          0% { transform: translate(0,0); opacity: 0; }
          6% { opacity: 1; }
          88% { opacity: 1; }
          96% { transform: translate(-452px,0); opacity: 0; }
          100% { transform: translate(-452px,0); opacity: 0; }
        }
        @keyframes ft-drive-e {
          0% { transform: translate(0,0); opacity: 0; }
          6% { opacity: 1; }
          88% { opacity: 1; }
          96% { transform: translate(452px,0); opacity: 0; }
          100% { transform: translate(452px,0); opacity: 0; }
        }

        @media (max-width: 620px) {
          .ft-header { align-items: flex-start; padding: 12px; }
          .ft-header-left, .ft-header-metrics { width: 100%; }
          .ft-header-metrics { justify-content: flex-start; }
          .ft-health-detail { margin-top: 0; text-align: left; }
          .ft-progress-card, .ft-card, .ft-view-card { padding: 12px; }
          .ft-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ft-bar-row { align-items: stretch; flex-wrap: wrap; }
          .ft-bar-label { flex: 1 1 auto; white-space: normal; }
          .ft-bar-track { flex: 1 0 100%; order: 3; }
          .ft-btn-row { gap: 10px; }
          .ft-btn, .ft-force-btn { min-height: 56px; }
          .ft-force-counter { align-items: flex-start; }
          .ft-view-title { line-height: 1.3; }
          .ft-svg-wrap { width: 100%; }
        }

        @media (max-width: 380px) {
          .ft-root { padding-inline: 4px; }
          .ft-header-icon { width: 34px; height: 34px; }
          .ft-latency-badge, .ft-force-badge { width: 100%; border-radius: 8px; white-space: normal; }
          .ft-progress-title { align-items: flex-start; flex-wrap: wrap; }
          .ft-force-counter { flex-direction: column; }
        }

        @media (prefers-reduced-motion: reduce) {
          .ft-root *, .ft-root *::before, .ft-root *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="ft-header">
        <div className="ft-header-left">
          <div className="ft-header-icon">🚦</div>
          <div>
            <p className="ft-title">FEUX TRICOLORES — 4 DIRECTIONS</p>
          </div>
        </div>
        {(isForcing || stale || !connected || liveDataError) && <div className="ft-header-metrics">
          {isForcing && (
            <div className="ft-force-badge">⚡ Forçage fixe</div>
          )}
          {(stale || !connected || liveDataError) && (
            <div className="ft-health-detail">
              Commandes désactivées
              {liveDataError ? ` — ${liveDataError}` : ""}
            </div>
          )}
        </div>}
      </div>

      {(stale || !connected || liveDataError) && (
        <div className={`ft-connection-alert ${!connected ? "disconnected" : ""}`} role="alert">
          <span aria-hidden="true">{connected ? "⚠" : "●"}</span>
          <span>
            {!connected ? "Backend déconnecté." : "Données temps réel indisponibles."}
            {" La dernière valeur connue reste affichée et toutes les commandes sont désactivées."}
            {liveDataError ? ` Détail : ${liveDataError}` : ""}
          </span>
        </div>
      )}

      <div className="ft-progress-card">
        <div className="ft-progress-top">
          <div className="ft-progress-title">
            📍 Progression du Cycle
            {isForcing && <span className="pill">FORÇAGE</span>}
          </div>
          <div className="ft-timer" aria-label="Temps restant estimé">
            {effectiveStep && !isForcing ? `${timeLeft}s restantes` : isForcing ? "Cycle suspendu" : "En attente"}
          </div>
        </div>

        <div className="ft-steps">
          <div className={`ft-step-box ns ${effectiveStep === "NS_GREEN" ? "active" : ""}`}>
            <div className="lbl">NS Vert</div><div className="dur">{stepSeconds.NS_GREEN}s</div>
          </div>
          <div className={`ft-step-box ns ${effectiveStep === "NS_ORANGE" ? "active" : ""}`}>
            <div className="lbl">NS Orange</div><div className="dur">{stepSeconds.NS_ORANGE}s</div>
          </div>
          <div className={`ft-step-box eo ${effectiveStep === "EO_GREEN" ? "active" : ""}`}>
            <div className="lbl">EO Vert</div><div className="dur">{stepSeconds.EO_GREEN}s</div>
          </div>
          <div className={`ft-step-box eo ${effectiveStep === "EO_ORANGE" ? "active" : ""}`}>
            <div className="lbl">EO Orange</div><div className="dur">{stepSeconds.EO_ORANGE}s</div>
          </div>
        </div>

        <div className="ft-bar-row">
          <div className="ft-bar-label">{isForcing ? "Forçage actif" : effectiveStep ? STEP_LABEL[effectiveStep] : "—"}</div>
          <div className="ft-bar-track">
            <div className={`ft-bar-fill ${isForcing ? "forced" : ""}`} style={{ width: `${progressPct}%` }} />
          </div>
          {isForcing && <div className="ft-bar-end">FIXE</div>}
        </div>
      </div>

      <div className="ft-grid">
        <div className="ft-controls-col">
          <div className="ft-card">
            <div className="ft-card-title">⚙ Pupitre de Commande</div>
            <div className="ft-btn-row">
              <button className="ft-btn start" onClick={handleStart} disabled={controlsDisabled || running}>
                <span className="ico">▶</span> MARCHE
                <span className="sub">S1</span>
              </button>
              <button className="ft-btn stop" onClick={handleStop} disabled={controlsDisabled || !running}>
                <span className="ico">■</span> ARRÊT
                <span className="sub">S2</span>
              </button>
            </div>
          </div>

          <div className="ft-card">
            <div className="ft-card-title">⚡ Forçage Manuel</div>
            <ForceButton
              on={displayForcage}
              label="Impulsion de forçage"
              sublabel="Changement de sens · 500 ms"
              onClick={pulseForcage}
              disabled={controlsDisabled || isSendingForcage}
            />
            <div className="ft-force-counter">
              <span className="lbl">🔁 Nbr de changements de sens total : </span>
              <span className="val ft-mono">{forcageCount}</span>
            </div>
            <div className="ft-force-counter session">
              <span className="lbl">🔁 Changement de sens par jours</span>
              <span className="val ft-mono">{sessionForcageCount}</span>
            </div>
            <div className="ft-plc-status">
              <span className={`ft-plc-dot ${running ? "on" : ""}`} />
              <span className={`ft-plc-dot ${running ? "on" : ""}`} />
              <span className={`ft-plc-dot ${running ? "on" : ""}`} />
              PLC {stale ? "INDISPONIBLE" : running ? "ACTIF" : "INACTIF"}
            </div>
          </div>

          <div className="ft-card">
            <div className="ft-card-title">⬇ Entrées PLC</div>
            {plcBits.map((b) => (
              <div className="ft-plc-row" key={b.label}>
                <span className="name"><span className={`dot ${b.value ? "on" : ""}`} />{b.label}</span>
                <span className={`val ${b.value ? "on" : ""}`}>{b.value}</span>
              </div>
            ))}
          </div>

          <div className="ft-card">
            <div className="ft-card-title">⬆ Sorties PLC</div>
            {plcOutputs.map((o) => (
              <div className="ft-plc-row" key={o.label}>
                <span className="name">
                  <span
                    className="dot"
                    style={{
                      background: o.value ? o.color : "#d2d4d4",
                      boxShadow: o.value ? `0 0 5px ${o.color}` : "none",
                    }}
                  />
                  {o.label}
                </span>
                <span className={`val ${o.value ? "on" : ""}`} style={o.value ? { color: o.color } : undefined}>
                  {o.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Colonne droite : vue intersection + Journal empilés dans l'espace libre ── */}
        <div className="ft-right-col">
          <div className="ft-view-card">
            <div className="ft-view-title">🚥 Vue Intersection Temps Réel</div>
            <div className={`ft-svg-wrap ${!running ? "stopped" : ""}`}>
              <IntersectionSVG nsColor={nsColor} eoColor={eoColor} nsMoving={nsMoving} eoMoving={eoMoving} />
            </div>
          </div>
          <div className="ft-card">
            <div className="ft-card-title">▤ Journal d'activité</div>
            <div className="ft-log-list" aria-label="Journal d'activité des feux tricolores">
              {log.map((entry) => (
                <div className={`ft-log-entry ${entry.type}`} key={entry.id}>
                  <span className={`ft-log-icon-badge ${entry.type}`} aria-hidden="true">
                    {LOG_ICON[entry.type] || LOG_ICON.info}
                  </span>
                  <span className="ft-log-body">
                    <span className="ft-log-time ft-mono">{entry.time}</span>
                    <span className="ft-log-text">{entry.text}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SVG INTERSECTION
   ============================================================ */
function IntersectionSVG({ nsColor, eoColor, nsMoving, eoMoving }) {
  return (
    <svg viewBox="0 0 600 600" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="600" height="600" fill="#dfe4e4" />

      <circle cx="130" cy="130" r="85" fill="#cfe3d6" stroke="#a9cdb8" strokeWidth="2" />
      <circle cx="470" cy="130" r="85" fill="#cfe3d6" stroke="#a9cdb8" strokeWidth="2" />
      <circle cx="130" cy="470" r="85" fill="#cfe3d6" stroke="#a9cdb8" strokeWidth="2" />
      <circle cx="470" cy="470" r="85" fill="#cfe3d6" stroke="#a9cdb8" strokeWidth="2" />

      <rect x="220" y="0" width="160" height="600" fill="#4a5258" />
      <rect x="0" y="220" width="600" height="160" fill="#4a5258" />
      <rect x="220" y="220" width="160" height="160" fill="#42494e" />

      {/* Pointillés de voie — passés de jaune à blanc */}
      <line x1="300" y1="0" x2="300" y2="210" stroke="#ffffff" strokeWidth="4" strokeDasharray="14 10" />
      <line x1="300" y1="390" x2="300" y2="600" stroke="#ffffff" strokeWidth="4" strokeDasharray="14 10" />
      <line x1="0" y1="300" x2="210" y2="300" stroke="#ffffff" strokeWidth="4" strokeDasharray="14 10" />
      <line x1="390" y1="300" x2="600" y2="300" stroke="#ffffff" strokeWidth="4" strokeDasharray="14 10" />

      <rect x="300" y="216" width="78" height="6" fill="#f4f4f4" opacity="0.9" />
      <rect x="222" y="378" width="78" height="6" fill="#f4f4f4" opacity="0.9" />
      <rect x="216" y="222" width="6" height="78" fill="#f4f4f4" opacity="0.9" />
      <rect x="378" y="300" width="6" height="78" fill="#f4f4f4" opacity="0.9" />

      {/* Labels cardinaux — déplacés sur le côté de la route (zone herbe), hors de l'axe central */}
      <text x="392" y="24" textAnchor="start" fill="#5f6b6b" fontSize="13" fontFamily="Share Tech Mono, monospace" letterSpacing="2">NORD</text>
      <text x="392" y="592" textAnchor="start" fill="#5f6b6b" fontSize="13" fontFamily="Share Tech Mono, monospace" letterSpacing="2">SUD</text>
      <text x="566" y="212" textAnchor="end" fill="#5f6b6b" fontSize="13" fontFamily="Share Tech Mono, monospace" letterSpacing="2">EST</text>
      <text x="34" y="212" textAnchor="start" fill="#5f6b6b" fontSize="13" fontFamily="Share Tech Mono, monospace" letterSpacing="2">OUEST</text>

      <Signal x={400} y={195} color={nsColor} />
      <Signal x={200} y={405} color={nsColor} />
      <Signal x={405} y={405} color={eoColor} orientation="horizontal" />
      <Signal x={195} y={195} color={eoColor} orientation="horizontal" />

      <g className={`ft-car ${nsMoving ? "drive-s" : ""}`} style={!nsMoving ? { opacity: 1 } : undefined}>
        <Car x={339} y={170} orientation="vertical" body="#e07a2b" />
      </g>
      <g className={`ft-car ${nsMoving ? "drive-n" : ""}`} style={!nsMoving ? { opacity: 1 } : undefined}>
        <Car x={261} y={410} orientation="vertical" body="#3270c9" />
      </g>
      <g className={`ft-car ${eoMoving ? "drive-w" : ""}`} style={!eoMoving ? { opacity: 1 } : undefined}>
        <Car x={400} y={261} orientation="horizontal" body="#8a4fd6" />
      </g>
      <g className={`ft-car ${eoMoving ? "drive-e" : ""}`} style={!eoMoving ? { opacity: 1 } : undefined}>
        <Car x={170} y={339} orientation="horizontal" body="#c4392b" />
      </g>
    </svg>
  );
}

function Signal({ x, y, color, orientation = "vertical" }) {
  const states = {
    red: color === "red",
    orange: color === "orange",
    green: color === "green",
  };
  const colOf = (c, on, off) => (on ? c : off);

  if (orientation === "horizontal") {
    return (
      <g>
        <rect x={x - 39} y={y - 14} width="78" height="28" rx="7" fill="#2b2f31" stroke="#1a1c1d" strokeWidth="1.5" />
        <circle cx={x - 22} cy={y} r="8" fill={colOf("#ff4d4f", states.red, "#5a2b2d")} style={states.red ? { filter: "drop-shadow(0 0 5px #ff4d4f)" } : undefined} />
        <circle cx={x} cy={y} r="8" fill={colOf("#ffb020", states.orange, "#5c4a23")} style={states.orange ? { filter: "drop-shadow(0 0 5px #ffb020)" } : undefined} />
        <circle cx={x + 22} cy={y} r="8" fill={colOf("#2ecc71", states.green, "#234a33")} style={states.green ? { filter: "drop-shadow(0 0 5px #2ecc71)" } : undefined} />
        <line x1={x} y1={y + 14} x2={x} y2={y + 30} stroke="#1a1c1d" strokeWidth="3" />
      </g>
    );
  }

  return (
    <g>
      <rect x={x - 14} y={y - 38} width="28" height="78" rx="7" fill="#2b2f31" stroke="#1a1c1d" strokeWidth="1.5" />
      <circle cx={x} cy={y - 22} r="8" fill={colOf("#ff4d4f", states.red, "#5a2b2d")} style={states.red ? { filter: "drop-shadow(0 0 5px #ff4d4f)" } : undefined} />
      <circle cx={x} cy={y} r="8" fill={colOf("#ffb020", states.orange, "#5c4a23")} style={states.orange ? { filter: "drop-shadow(0 0 5px #ffb020)" } : undefined} />
      <circle cx={x} cy={y + 22} r="8" fill={colOf("#2ecc71", states.green, "#234a33")} style={states.green ? { filter: "drop-shadow(0 0 5px #2ecc71)" } : undefined} />
      <line x1={x} y1={y + 40} x2={x} y2={y + 56} stroke="#1a1c1d" strokeWidth="3" />
    </g>
  );
}

function Car({ x, y, orientation, body }) {
  const w = orientation === "vertical" ? 18 : 30;
  const h = orientation === "vertical" ? 30 : 18;
  return (
    <g transform={`translate(${x - w / 2}, ${y - h / 2})`}>
      <rect x="0" y="0" width={w} height={h} rx="5" fill={body} stroke="#2b2c2c" strokeWidth="1" />
      {orientation === "vertical" ? (
        <>
          <rect x="2.5" y={h * 0.32} width={w - 5} height={h * 0.28} rx="2" fill="#cfe9f5" opacity="0.85" />
          <circle cx="2" cy={h * 0.18} r="2.4" fill="#1a1a1a" />
          <circle cx={w - 2} cy={h * 0.18} r="2.4" fill="#1a1a1a" />
          <circle cx="2" cy={h * 0.82} r="2.4" fill="#1a1a1a" />
          <circle cx={w - 2} cy={h * 0.82} r="2.4" fill="#1a1a1a" />
        </>
      ) : (
        <>
          <rect x={w * 0.32} y="2.5" width={w * 0.28} height={h - 5} rx="2" fill="#cfe9f5" opacity="0.85" />
          <circle cx={w * 0.18} cy="2" r="2.4" fill="#1a1a1a" />
          <circle cx={w * 0.18} cy={h - 2} r="2.4" fill="#1a1a1a" />
          <circle cx={w * 0.82} cy="2" r="2.4" fill="#1a1a1a" />
          <circle cx={w * 0.82} cy={h - 2} r="2.4" fill="#1a1a1a" />
        </>
      )}
    </g>
  );
}
